import mixpanel from 'mixpanel-browser';

let mixpanelInitialized = false;

const detectPlatform = (): 'mobile' | 'desktop' => {
  if (typeof navigator === 'undefined') return 'desktop';
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
};

const detectLanguage = (): string => {
  if (typeof navigator === 'undefined') return 'fr';
  return navigator.language?.split('-')[0] ?? 'fr';
};

export function initializeMixpanel(): void {
  if (mixpanelInitialized) {
    console.log('[Consent] Mixpanel is already initialized.');
    return;
  }

  const token = import.meta.env.VITE_MIXPANEL_TOKEN;
  if (!token) {
    console.warn('[Mixpanel] No VITE_MIXPANEL_TOKEN found, skipping initialization.');
    return;
  }

  mixpanel.init(token, {
    track_pageview: false,
    persistence: 'localStorage',
    api_host: 'https://api-eu.mixpanel.com',
  });

  // Super properties: envoyées avec TOUS les events
  mixpanel.register({
    platform: detectPlatform(),
    app_version: import.meta.env.APP_VERSION ?? 'unknown',
    language: detectLanguage(),
  });

  mixpanelInitialized = true;
  console.log('[Consent] initialized Mixpanel.');
}

const INTERNAL_EMAIL_DOMAIN = 'shareheritage.com';

function isInternalEmail(email: string): boolean {
  return email.toLowerCase().endsWith(`@${INTERNAL_EMAIL_DOMAIN}`);
}

export function identifyMixpanelUser(user: {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  country?: string;
}): void {
  if (!mixpanelInitialized) return;

  const isInternal = isInternalEmail(user.email);

  mixpanel.identify(user.id);
  mixpanel.people.set({
    $email: user.email,
    $first_name: user.firstName,
    $last_name: user.lastName,
    $name: [user.firstName, user.lastName].filter(Boolean).join(' '),
    is_internal: isInternal,
    ...(user.country && { country: user.country }),
  });

  // Ajoute user_id et is_internal comme super properties (ré-envoyés sur chaque event)
  mixpanel.register({
    user_id: user.id,
    is_internal: isInternal,
    ...(user.country && { country: user.country }),
  });
}

export function trackMixpanelEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!mixpanelInitialized) return;

  mixpanel.track(eventName, properties);
}

export function resetMixpanel(): void {
  if (!mixpanelInitialized) return;

  mixpanel.reset();
}
